<template>
  <div class="d-flex flex-column ga-5">
    <div>
      <div class="d-flex align-center mb-2">
        <span
          class="text-uppercase font-size-11 font-weight-bold text-disabled">
          Тип договору
        </span>
        <span class="text-error font-weight-bold ml-1">*</span>
      </div>
      <b-select
        v-model="document.typeId"
        :error-messages="getErrorMessages(v$.typeId)"
        :items="$directory.get('documentTypes')"
        :loading="$loading.isLoading('documentTypes')"
        item-title="name"
        item-value="id"
        placeholder="Оберіть зі списку"
        @blur="v$.typeId.$touch()"
        @focus="$directory.fill('documentTypes')">
      </b-select>
    </div>

    <div>
      <div class="d-flex align-center mb-2">
        <span
          class="text-uppercase font-size-11 font-weight-bold text-disabled">
          Документ
        </span>
        <span class="text-error font-weight-bold ml-1">*</span>
      </div>
      <b-input-file
        v-model="document.document"
        :accept="['.pdf', '.docx', '.xlsx']"
        :show-files="false"
        :v$="v$.document"
        dropper
        main-folder="Документы"
        name-folder="template">
        <template #dropper>
          <v-hover v-slot="{ isHovering, props }">
            <v-card
              v-bind="props"
              :class="{ 'bg-blue-lighten-5': isHovering }"
              class="dropper-card text-center cursor-pointer"
              flat>
              <div class="mb-3">
                <v-icon
                  color="grey-lighten-1"
                  icon="mdi-tray-arrow-up"
                  size="40"></v-icon>
              </div>
              <div class="font-size-15 font-weight-bold mb-1">
                Перетягніть файл або натисніть
              </div>
              <div class="text-disabled font-size-13">
                PDF, DOCX, XLSX · до 20 МБ
              </div>
            </v-card>
          </v-hover>
        </template>
      </b-input-file>

      <v-card
        v-if="selectedFile"
        class="selected-file d-flex align-center ga-3 mt-3"
        color="green-lighten-5"
        flat>
        <v-avatar
          :color="getFileColor(selectedFile.ext)"
          rounded="lg"
          size="40">
          <span class="text-white font-size-11 font-weight-bold text-uppercase">
            {{ selectedFile.ext }}
          </span>
        </v-avatar>
        <div class="flex-grow-1 overflow-hidden">
          <div class="font-size-14 font-weight-bold text-truncate">
            {{ selectedFile.name }}
          </div>
          <div class="text-disabled font-size-12">
            {{ formatFileSize(selectedFile.size) }}
          </div>
        </div>
        <v-btn
          density="comfortable"
          icon="mdi-close"
          size="small"
          variant="text"
          @click="removeFile"></v-btn>
      </v-card>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, watch } from 'vue'
import { required, useVuelidate } from '@/plugins/index.js'
import { documentAgreementCreate } from '@/requests/documentAgreement'
import { cachedObject } from 'best-modules/utils'
import { getErrorMessages } from '@/utils/helpers.js'
import { setSnackbar } from 'best-modules/plugins'
import { fillDirectory } from '@/directory'
import { BInputFile } from 'best-modules/components'

export default {
  components: {
    BInputFile,
  },
  props: {
    dialog: {
      type: Object,
      required: true,
    },
  },
  methods: { getErrorMessages },
  async setup() {
    const document = cachedObject<{
      typeId: number | null
      name: string | null
      document: any
    }>({
      typeId: null,
      name: null,
      document: null,
    })

    const rules = {
      typeId: { required },
      document: { required },
    }
    const v$ = useVuelidate(rules, document)

    const selectedFile = computed(() => {
      return Array.isArray(document.document) ? document.document[0] : null
    })

    watch(
      () => document.document,
      value => {
        if (Array.isArray(value) && value.length > 1) {
          document.document = [value[value.length - 1]]
          return
        }
        const file = Array.isArray(value) ? value[0] : null
        document.name = file ? file.name.replace(/\.[^./\\]+$/, '') : null
      }
    )

    await fillDirectory('documentTypes')

    const removeFile = () => {
      document.document = null
    }

    const getFileColor = (ext: string): string => {
      switch (ext) {
        case 'pdf':
          return 'green-darken-1'
        case 'docx':
          return 'blue-darken-1'
        case 'xlsx':
          return 'teal-darken-1'
        default:
          return 'grey-darken-1'
      }
    }

    const formatFileSize = (bytes: number): string => {
      if (!bytes) return '0 Б'
      const units = ['Б', 'КБ', 'МБ', 'ГБ']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return `${parseFloat((bytes / Math.pow(1024, i)).toFixed(1))} ${units[i]}`
    }

    const submit = () => {
      return documentAgreementCreate(document.$object).then(res => {
        setSnackbar({ text: 'Документ успішно створено' })

        return res
      })
    }

    return {
      v$,
      document,
      selectedFile,
      removeFile,
      getFileColor,
      formatFileSize,
      submit,
    }
  },
}
</script>

<style lang="scss" scoped>
.dropper-card {
  border: 1.5px dashed #d5d5e0;
  border-radius: 12px;
  padding: 32px 24px;
  background-color: #fcfcfd;
  transition: background-color 0.2s;
}

.selected-file {
  border: 1px solid #c8e6c9;
  border-radius: 12px;
  padding: 12px 16px;
}
</style>
